/**
 * Virtualized Message List
 * 
 * Renders only visible messages to prevent lag with large sessions.
 * Uses intersection observer for efficient rendering.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Message, MessageDensity, User, GlobalConfig } from '../types';
import ChatMessage from './ChatMessage';
import { Attachment } from '../types';

interface VirtualizedMessageListProps {
    messages: Message[];
    density: MessageDensity;
    isDarkMode: boolean;
    user?: User;
    config?: GlobalConfig;
    pinnedMessageIds?: string[];
    rewardedMessageIds?: Set<string>;
    onFeedback?: (messageId: string, type: 'up' | 'down') => Promise<any>;
    onRegenerate?: () => void;
    onPreview?: (attachment: Attachment) => void;
    onTogglePin?: (messageId: string) => void;
    onAIPersonaClick?: () => void;
    onExport?: (message: Message) => void;
    userName?: string;
    onOpenQuiz?: (quiz: any) => void;
    showTimestamps?: boolean;
    showAvatars?: boolean;
}

// Message height estimates based on density
const MESSAGE_HEIGHT_ESTIMATES = {
    compact: 120,
    comfortable: 160,
    spacious: 200,
};

// Buffer for rendering extra messages above/below viewport
const BUFFER_SIZE = 5;

const VirtualizedMessageList: React.FC<VirtualizedMessageListProps> = ({
    messages,
    density,
    isDarkMode,
    user,
    config,
    pinnedMessageIds = [],
    rewardedMessageIds = new Set(),
    onFeedback,
    onRegenerate,
    onPreview,
    onTogglePin,
    onAIPersonaClick,
    onExport,
    userName,
    onOpenQuiz,
    showTimestamps,
    showAvatars,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: Math.min(20, messages.length) });
    const [isAutoScrolling, setIsAutoScrolling] = useState(true);
    const lastMessageCountRef = useRef(messages.length);

    // Detect if any message is currently streaming
    const isStreaming = messages.some(m => m.isStreaming);

    // Estimate container height
    const estimatedItemHeight = MESSAGE_HEIGHT_ESTIMATES[density];
    const totalHeight = messages.length * estimatedItemHeight;

    // Update visible range based on scroll
    const updateVisibleRange = useCallback(() => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const scrollTop = container.scrollTop;
        const viewportHeight = container.clientHeight;

        const startIndex = Math.max(0, Math.floor(scrollTop / estimatedItemHeight) - BUFFER_SIZE);
        const endIndex = Math.min(
            messages.length,
            Math.ceil((scrollTop + viewportHeight) / estimatedItemHeight) + BUFFER_SIZE
        );

        setVisibleRange({ start: startIndex, end: endIndex });

        // Check if user is near bottom for auto-scroll
        const isNearBottom = scrollTop + viewportHeight >= container.scrollHeight - 200;
        setIsAutoScrolling(isNearBottom);
    }, [messages.length, estimatedItemHeight]);

    // Scroll to bottom when new messages arrive or during streaming
    useEffect(() => {
        if (!isAutoScrolling) {
            lastMessageCountRef.current = messages.length;
            return;
        }

        if (isStreaming) {
            // During streaming: use instant scrollTop via rAF for jank-free updates
            requestAnimationFrame(() => {
                if (containerRef.current) {
                    containerRef.current.scrollTop = containerRef.current.scrollHeight;
                }
            });
        } else if (messages.length > lastMessageCountRef.current) {
            // New message arrived (not streaming): smooth scroll
            requestAnimationFrame(() => {
                if (containerRef.current) {
                    containerRef.current.scrollTo({
                        top: containerRef.current.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            });
        }
        lastMessageCountRef.current = messages.length;
    }, [messages, isAutoScrolling, isStreaming]);

    // Set up scroll listener
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Initial render - scroll to bottom
        container.scrollTop = container.scrollHeight;
        updateVisibleRange();

        // Throttled scroll handler
        let ticking = false;
        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    updateVisibleRange();
                    ticking = false;
                });
                ticking = true;
            }
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, [updateVisibleRange]);

    // Reset range when messages change significantly
    useEffect(() => {
        if (messages.length <= 20) {
            setVisibleRange({ start: 0, end: messages.length });
        } else {
            updateVisibleRange();
        }
    }, [messages.length, updateVisibleRange]);

    // Get visible messages
    const visibleMessages = useMemo(() => {
        return messages.slice(visibleRange.start, visibleRange.end);
    }, [messages, visibleRange]);

    // Calculate spacer heights
    const topSpacerHeight = visibleRange.start * estimatedItemHeight;
    const bottomSpacerHeight = (messages.length - visibleRange.end) * estimatedItemHeight;

    // For small message lists, render normally without virtualization
    if (messages.length <= 30) {
        return (
            <div
                ref={containerRef}
                className="flex-1 overflow-y-auto overflow-x-hidden chat-scroll-container"
                style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}
            >
                <div className="flex flex-col pb-4 max-w-3xl mx-auto w-full">
                    {messages.map((msg, index) => {
                        const isLast = index === messages.length - 1;
                        return (
                            <div
                                key={msg.id}
                                style={!isLast && !msg.isStreaming ? {
                                    contentVisibility: 'auto',
                                    containIntrinsicSize: '0 150px'
                                } as React.CSSProperties : undefined}
                            >
                                <ChatMessage
                                    message={msg}
                                    isLast={isLast}
                                    density={density}
                                    isDarkMode={isDarkMode}
                                    user={user}
                                    config={config}
                                    isPinned={pinnedMessageIds.includes(msg.id)}
                                    hasBeenRewarded={rewardedMessageIds.has(msg.id)}
                                    onFeedback={onFeedback}
                                    onRegenerate={isLast ? onRegenerate : undefined}
                                    onPreview={onPreview}
                                    onTogglePin={onTogglePin}
                                    onAIPersonaClick={onAIPersonaClick}
                                    onExport={onExport}
                                    userName={userName}
                                    onOpenQuiz={onOpenQuiz}
                                    showTimestamps={showTimestamps}
                                    showAvatars={showAvatars}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="flex-1 overflow-y-auto overflow-x-hidden pb-4 scrollbar-thin ios-scroll chat-scroll-container"
            style={{
                touchAction: 'pan-y',
                willChange: 'scroll-position',
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain'
            }}
        >
            <div
                className="flex flex-col relative max-w-3xl mx-auto w-full"
                style={{ minHeight: totalHeight }}
            >
                {/* Top spacer */}
                {topSpacerHeight > 0 && (
                    <div style={{ height: topSpacerHeight, flexShrink: 0 }} />
                )}

                {/* Visible messages */}
                {visibleMessages.map((msg, index) => {
                    const actualIndex = visibleRange.start + index;
                    return (
                        <ChatMessage
                            key={msg.id}
                            message={msg}
                            isLast={actualIndex === messages.length - 1}
                            density={density}
                            isDarkMode={isDarkMode}
                            user={user}
                            config={config}
                            isPinned={pinnedMessageIds.includes(msg.id)}
                            hasBeenRewarded={rewardedMessageIds.has(msg.id)}
                            onFeedback={onFeedback}
                            onRegenerate={actualIndex === messages.length - 1 ? onRegenerate : undefined}
                            onPreview={onPreview}
                            onTogglePin={onTogglePin}
                            onAIPersonaClick={onAIPersonaClick}
                            onExport={onExport}
                            onOpenQuiz={onOpenQuiz}
                            showTimestamps={showTimestamps}
                            showAvatars={showAvatars}
                        />
                    );
                })}

                {/* Bottom spacer */}
                {bottomSpacerHeight > 0 && (
                    <div style={{ height: bottomSpacerHeight, flexShrink: 0 }} />
                )}
            </div>

            {/* Message count indicator for large sessions */}
            {messages.length > 50 && (
                <div className={`fixed bottom-24 right-4 px-3 py-1.5 rounded-full text-xs font-medium shadow-lg ${isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-white text-slate-600'
                    }`}>
                    {visibleRange.start + 1}-{visibleRange.end} of {messages.length} messages
                </div>
            )}
        </div>
    );
};

export default VirtualizedMessageList;
